import { AdPlatformMappingCell } from 'app/components/contacts/AdPlatformMappingCell'
import { LazyAdCampaign } from 'app/components/contacts/LazyAdCampaign'
import { DateComponent } from 'app/components/DateComponent'
import {
  generalContactColumns,
  marketingReferralColumns,
  contactDetailColumns,
  contactIdentityColumns,
  nameColumn,
  locationColumn,
  tagsColumn,
  channelColumn,
  utmEditableColumns,
  scheduledProductionColumn,
  productionLtvColumn,
  collectedLtvColumn,
  appointmentReferralColumns,
  firstVisitValueColumn,
} from 'app/table-columns/sharedContactColumns'
import type { ContactColumnsFactory } from 'app/table-columns/sharedContactColumns'
import type { ContactJson } from 'app/types/entities'
import { getLeadStatusColorMap } from 'lib/colorMappings'

export const defaultColumns: ContactColumnsFactory = (
  contactHandler,
  updateContact,
  closeDrawerHandler,
  isSMC,
  isSuper,
) => [
  nameColumn(contactHandler),
  locationColumn(),
  {
    id: `leadStatus`,
    accessorKey: `leadStatus`,
    header: `Lead Status`,
    cell: (props) => (
      <>
        <span
          className="mr-1.5 inline-block h-2 w-2 rounded-[2px] align-middle"
          style={{ backgroundColor: getLeadStatusColorMap(props.row.original.leadStatus) }}
        ></span>
        <span>{props.row.original.leadStatus}</span>
      </>
    ),
    meta: {
      group: `Marketing`,
      defaultVisible: true,
      sortKey: `leadStatus.keyword`,
    },
  },
  ...(isSuper
    ? [
        {
          id: `neededAction`,
          accessorFn: (row) => (row.neededAction ? `Yes` : `No`),
          header: `Needed Action`,
          meta: {
            group: `Marketing`,
            defaultVisible: true,
            sortKey: `neededAction`,
          },
        },
      ]
    : []),
  {
    id: `lifecycleStage`,
    accessorKey: `lifecycleStage`,
    header: `Lifecycle Stage`,
    meta: {
      group: `General`,
      defaultVisible: true,
      pinned: false,
      sortKey: `lifecycleStage.keyword`,
    },
  },
  tagsColumn<ContactJson>(),
  ...utmEditableColumns({ isSMC, updateContact, closeDrawerHandler, defaultVisible: true }),
  channelColumn,
  scheduledProductionColumn,
  productionLtvColumn,
  {
    id: `becameLeadDate`,
    accessorKey: `becameLeadDate`,
    header: `Became a Lead Date`,
    minSize: 120,
    cell: (props) => DateComponent({ value: props.row.original.becameLeadDate, dateOnly: true }),
    meta: {
      group: `Marketing`,
      defaultVisible: true,
      sortKey: `becameLeadDate`,
    },
  },
  {
    id: `firstAppointmentMadeDate`,
    accessorKey: `firstAppointmentMadeDate`,
    header: `Became a New Patient Scheduled Date`,
    minSize: 120,
    cell: (props) => DateComponent({ value: props.row.original.firstAppointmentMadeDate, dateOnly: true }),
    meta: {
      group: `Marketing`,
      defaultVisible: true,
      sortKey: `firstAppointmentMadeDate`,
    },
  },
  {
    id: `becamePatientDate`,
    accessorKey: `becamePatientDate`,
    header: `Became a Patient Date`,
    minSize: 120,
    cell: (props) => DateComponent({ value: props.row.original.becamePatientDate, dateOnly: true }),
    meta: {
      group: `PMS`,
      defaultVisible: true,
      sortKey: `becamePatientDate`,
    },
  },
  ...contactIdentityColumns,
  ...contactDetailColumns,
  {
    id: `Link Status`,
    accessorKey: `linkStatus`,
    header: `Link Status`,
    meta: {
      group: `PMS`,
      defaultVisible: false,
      sortKey: `linkStatus.keyword`,
    },
  },
  {
    id: `firstAppointmentDate`,
    accessorKey: `firstAppointmentDate`,
    header: `First Appointment Date`,
    minSize: 120,
    cell: (props) => <DateComponent value={props.row.original.firstAppointmentDate} dateOnly />,
    meta: {
      group: `PMS`,
      defaultVisible: false,
      sortKey: `firstAppointmentDate`,
    },
  },
  {
    id: `lastAppointmentDate`,
    accessorKey: `lastAppointmentDate`,
    header: `Last Appointment Date`,
    minSize: 120,
    cell: (props) => <DateComponent value={props.row.original.lastAppointmentDate} dateOnly />,
    meta: {
      group: `PMS`,
      defaultVisible: false,
      sortKey: `lastAppointmentDate`,
    },
  },
  {
    id: `Nearest Appointment Date`,
    accessorKey: `nearestAppointmentDate`,
    header: `Nearest Appointment Date`,
    minSize: 120,
    cell: (props) => <DateComponent value={props.row.original.nearestAppointmentDate} dateOnly />,
    meta: {
      group: `PMS`,
      defaultVisible: false,
      sortKey: `nearestAppointmentDate`,
    },
  },
  ...appointmentReferralColumns,
  collectedLtvColumn,
  firstVisitValueColumn,
  {
    id: `closedLostReason`,
    accessorKey: `closedLostReason`,
    header: `Closed Lost Reason`,
    meta: {
      group: `Marketing`,
      defaultVisible: false,
      sortKey: `closedLostReason.keyword`,
    },
  },
  ...marketingReferralColumns,
  ...generalContactColumns,
  {
    id: `firstActivityDate`,
    accessorKey: `firstActivityDate`,
    header: `First Activity Date`,
    minSize: 120,
    cell: (props) => <DateComponent value={props.row.original.firstActivityDate} dateOnly />,
    meta: {
      group: `General`,
      defaultVisible: false,
      sortKey: `firstActivityDate`,
    },
  },
  {
    id: `lastActivityDate`,
    accessorKey: `lastActivityDate`,
    header: `Last Activity Date`,
    minSize: 120,
    cell: (props) => <DateComponent value={props.row.original.lastActivityDate} dateOnly />,
    meta: {
      group: `General`,
      defaultVisible: false,
      pinned: false,
      sortKey: `lastActivityDate`,
    },
  },
  {
    id: `preferredContact`,
    accessorKey: `preferredContact`,
    header: `Preferred Contact`,
    meta: {
      sortKey: `preferredContact.keyword`,
      group: `Communication`,
      defaultVisible: false,
      pinned: false,
    },
  },
  {
    id: `preferredCommunicationMethod`,
    accessorKey: `preferredCommunicationMethod`,
    header: `Preferred Communication Method`,
    meta: {
      group: `Communication`,
      defaultVisible: false,
      pinned: false,
      sortKey: `preferredCommunicationMethod.keyword`,
    },
  },
  {
    id: `patientCreatedDate`,
    accessorKey: `patientCreatedDate`,
    header: `Patient Created Date`,
    minSize: 120,
    cell: (props) => DateComponent({ value: props.row.original.patientCreatedDate, dateOnly: true }),
    meta: {
      group: `PMS`,
      defaultVisible: false,
      sortKey: `patientCreatedDate`,
    },
  },
  {
    id: `becameInactiveDate`,
    header: `Became Inactive Date`,
    minSize: 120,
    cell: (props) => DateComponent({ value: props.row.original.becameInactiveDate, dateOnly: true }),
    meta: {
      group: `General`,
      defaultVisible: false,
      sortKey: `becameInactiveDate`,
    },
  },
  {
    id: `firstActivityType`,
    header: `First Activity Type`,
    accessorKey: `firstActivityType`,
    meta: {
      group: `Marketing`,
      defaultVisible: false,
      sortKey: `firstActivityType.keyword`,
    },
  },
  {
    id: `First Transaction Date`,
    accessorKey: `firstTransactionDate`,
    header: `First Transaction Date`,
    minSize: 120,
    cell: (props) => DateComponent({ value: props.row.original.firstTransactionDate, dateOnly: true }),
    meta: {
      group: `PMS`,
      defaultVisible: false,
      sortKey: `firstTransactionDate`,
    },
  },
  {
    id: `Last Transaction Date`,
    accessorKey: `lastTransactionDate`,
    header: `Last Transaction Date`,
    minSize: 120,
    cell: (props) => DateComponent({ value: props.row.original.lastTransactionDate, dateOnly: true }),
    meta: {
      group: `PMS`,
      defaultVisible: false,
      sortKey: `lastTransactionDate`,
    },
  },
  {
    id: `Ad Click Date`,
    accessorKey: `adClickDate`,
    header: `Ad Click Date`,
    minSize: 120,
    cell: (props) => DateComponent({ value: props.row.original.adClickDate, dateOnly: true }),
    meta: {
      group: `Marketing`,
      defaultVisible: false,
      sortKey: `adClickDate`,
    },
  },
  {
    id: `Needs Review`,
    accessorKey: `needsReview`,
    header: `Needs Review`,
    meta: {
      group: `General`,
      defaultVisible: false,
      sortKey: `needsReview`,
    },
  },
  {
    id: `Recare Count`,
    accessorKey: `recareCount`,
    header: `Recare Count`,
    meta: {
      group: `PMS`,
      defaultVisible: false,
      sortKey: `recareCount`,
    },
  },
  {
    id: `schedulingActivityType`,
    header: `Scheduling Activity Type`,
    accessorKey: `schedulingActivityType`,
    meta: {
      group: `Marketing`,
      defaultVisible: false,
      sortKey: `schedulingActivityType.keyword`,
    },
  },
  {
    id: `Has Activity`,
    header: `Has Activity`,
    accessorFn: (props) => props.activityTypes,
    cell: (props) => <>{props.getValue()?.join(`, `)}</>,
    meta: {
      group: `Marketing`,
      defaultVisible: false,
    },
    enableSorting: false,
  },
  {
    id: `platform`,
    header: `Ad Platform`,
    accessorKey: `platform`,
    meta: {
      group: `Marketing`,
      defaultVisible: false,
      sortKey: `platform.keyword`,
      ControlledComponent: (row) => <AdPlatformMappingCell contact={row} />,
    },
  },
  {
    id: `Ad Campaign`,
    header: `Ad Campaign`,
    accessorKey: `trackableCampaignId`,
    enableSorting: false,
    cell: (props) => <LazyAdCampaign campaignId={props.getValue()} />,
    meta: {
      group: `Marketing`,
      defaultVisible: false,
    },
  },
  {
    id: `reportedAttribution`,
    header: `Reported Attribution`,
    accessorKey: `reportedAttribution`,
    meta: {
      getCellStyle: () => `lowercase`,
      group: `Marketing`,
      defaultVisible: false,
      sortKey: `reportedAttribution.keyword`,
    },
  },
]
